import { useState, useCallback, useRef } from 'react';

export type BlurStrength = 'low' | 'medium' | 'high';

export interface BackgroundOption {
  type: 'none' | 'blur' | 'image';
  blurStrength?: BlurStrength;
  imageUrl?: string;
  imageName?: string;
}

interface UseVirtualBackgroundOptions {
  meetingSession: any; // MeetingSession from Chime SDK
}

interface UseVirtualBackgroundReturn {
  currentBackground: BackgroundOption;
  isApplying: boolean;
  isSupported: boolean;
  error: string | null;
  applyBackground: (option: BackgroundOption) => Promise<void>;
  removeBackground: () => Promise<void>;
}

// Preset background images
export const PRESET_BACKGROUNDS: { id: string; name: string; url: string }[] = [
  { id: 'office', name: 'Office', url: '/backgrounds/office.jpg' },
  { id: 'bookshelf', name: 'Bookshelf', url: '/backgrounds/bookshelf.jpg' },
  { id: 'abstract-blue', name: 'Abstract Blue', url: '/backgrounds/abstract-blue.jpg' },
  { id: 'abstract-gray', name: 'Abstract Gray', url: '/backgrounds/abstract-gray.jpg' },
];

// Blur strength values for Chime SDK
const BLUR_STRENGTH_VALUES: Record<BlurStrength, number> = {
  low: 7,
  medium: 15,
  high: 25,
};

export function useVirtualBackground({
  meetingSession,
}: UseVirtualBackgroundOptions): UseVirtualBackgroundReturn {
  const [currentBackground, setCurrentBackground] = useState<BackgroundOption>({ type: 'none' });
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const processorRef = useRef<any>(null);
  const originalDeviceRef = useRef<string | null>(null);

  // Check if virtual backgrounds are supported
  const isSupported = typeof window !== 'undefined' && 
    'VideoFrame' in window && 
    'OffscreenCanvas' in window;

  const loadImage = useCallback((url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load background image'));
      img.src = url;
    });
  }, []);

  const applyBackground = useCallback(
    async (option: BackgroundOption) => {
      if (!meetingSession) {
        setError('Meeting session not available');
        return;
      }

      if (!isSupported) {
        setError('Virtual backgrounds are not supported on this device');
        return;
      }

      setIsApplying(true);
      setError(null);

      try {
        const audioVideo = meetingSession.audioVideo;
        const logger = meetingSession.logger;

        // Store original device if not already stored
        if (!originalDeviceRef.current) {
          const currentDevice = audioVideo.getActiveVideoInputDevice();
          if (currentDevice && typeof currentDevice === 'string') {
            originalDeviceRef.current = currentDevice;
          }
        }

        // Clean up existing processor
        if (processorRef.current) {
          try {
            await processorRef.current.destroy();
          } catch (e) {
            console.warn('Error destroying previous processor:', e);
          }
          processorRef.current = null;
        }

        if (option.type === 'none') {
          // Remove any transform and use original device
          if (originalDeviceRef.current) {
            await audioVideo.startVideoInput(originalDeviceRef.current);
          }
          setCurrentBackground({ type: 'none' });
          return;
        }

        // Dynamically import Chime SDK processors
        const {
          BackgroundBlurVideoFrameProcessor,
          BackgroundReplacementVideoFrameProcessor,
          DefaultVideoTransformDevice,
        } = await import('amazon-chime-sdk-js');

        let processor;

        if (option.type === 'blur') {
          const strength = BLUR_STRENGTH_VALUES[option.blurStrength || 'medium'];
          processor = await BackgroundBlurVideoFrameProcessor.create(logger, {
            blurStrength: strength,
          });
        } else if (option.type === 'image' && option.imageUrl) {
          const image = await loadImage(option.imageUrl);
          // BackgroundReplacementVideoFrameProcessor.create takes the image as the second parameter
          processor = await BackgroundReplacementVideoFrameProcessor.create(logger, image as any);
        }

        if (processor) {
          processorRef.current = processor;

          const deviceId = originalDeviceRef.current || 'default';
          const transformDevice = new DefaultVideoTransformDevice(logger, deviceId, [processor]);
          
          await audioVideo.startVideoInput(transformDevice);
          setCurrentBackground(option);
        }
      } catch (err: any) {
        console.error('Error applying virtual background:', err);
        setError(err.message || 'Failed to apply virtual background');
      } finally {
        setIsApplying(false);
      }
    },
    [meetingSession, isSupported, loadImage]
  );

  const removeBackground = useCallback(async () => {
    await applyBackground({ type: 'none' });
  }, [applyBackground]);

  return {
    currentBackground,
    isApplying,
    isSupported,
    error,
    applyBackground,
    removeBackground,
  };
}
